/**
 * catalog-schema.ts — Product/SKU master + alias mapping (T092)
 *
 * กติกา (CONTEXT §5.2, §7):
 * - SKU canonical ต่อ business; ชื่อ/รหัสจากช่องทางผูกผ่าน alias ไม่รวมชื่อเองโดยเดา
 * - SKU ที่ไม่มีต้นทุนที่มีผล ณ วันที่อ้างอิง = ข้อมูลยังไม่สมบูรณ์ (แสดงสีแดง)
 */
import { z } from 'zod';
import { uuid, dateKey, salesChannelCode, pagination } from './schemas.js';
import { resolveEffectiveCost, type CostHistoryRow } from './cost-schema.js';

export const skuInput = z.object({
  skuCode: z.string().trim().min(1, 'กรอกรหัส SKU').max(120),
  name: z.string().trim().max(300).optional(),
  unit: z.string().trim().max(40).optional(),
  productId: uuid.optional(),
});
export type SkuInput = z.infer<typeof skuInput>;

export const skuListFilter = z
  .object({
    search: z.string().trim().max(200).optional(),
    /** true = แสดงเฉพาะ SKU ที่ไม่มีต้นทุน ณ asOf */
    missingCostOnly: z.coerce.boolean().optional(),
    /** วันที่ใช้ตัดสินว่ามีต้นทุนหรือไม่ (default = วันนี้ ฝั่งผู้เรียก) */
    asOf: dateKey.optional(),
  })
  .and(pagination);
export type SkuListFilter = z.infer<typeof skuListFilter>;

export const skuAliasInput = z.object({
  skuId: uuid,
  shopId: uuid,
  channelCode: salesChannelCode,
  externalSkuId: z.string().trim().min(1, 'กรอกรหัสสินค้าจากช่องทาง').max(200),
  externalName: z.string().trim().max(300).optional(),
});
export type SkuAliasInput = z.infer<typeof skuAliasInput>;

/** SKU พร้อมสถานะต้นทุน ณ วันที่อ้างอิง (ใช้ทั้งหน้า SKU และคิว data quality) */
export interface SkuCostStatus {
  id: string;
  skuCode: string;
  name: string | null;
  unit: string;
  isActive: boolean;
  /** ต้นทุนที่มีผล ณ asOf (null = ยังไม่มีต้นทุน → แดง) */
  unitCost: string | null;
  hasCost: boolean;
  historyCount: number;
}

export interface SkuWithHistory {
  id: string;
  sku_code: string;
  name: string | null;
  unit: string;
  is_active: boolean;
  history: CostHistoryRow[];
}

/** map SKU + ประวัติต้นทุน → สถานะต้นทุน ณ asOf (ไม่มีต้นทุน = incomplete) */
export function toCostStatus(row: SkuWithHistory, asOf: string): SkuCostStatus {
  const unitCost = resolveEffectiveCost(row.history, asOf);
  return {
    id: row.id,
    skuCode: row.sku_code,
    name: row.name,
    unit: row.unit,
    isActive: row.is_active,
    unitCost,
    hasCost: unitCost !== null,
    historyCount: row.history.length,
  };
}
